import React, { type RefObject } from "react";
import { Typography } from "@altimateai/lego";
import type { SessionMessage } from "./types";
import { MessageBubble, parseMessageContent, getRoleLabel } from "./WorkflowSessionMessages";
import { formatTime } from "./sessionDetailHelpers";
import styles from "./workflow.module.scss";

interface Participant {
  name: string;
  role: string;
  isSlack: boolean;
}

interface Props {
  visibleMessages: SessionMessage[];
  participants: Participant[];
  syncAgo: string;
  hiddenCount: number;
  showSystemMessages: boolean;
  onToggleSystemMessages: () => void;
  isWaitingForReply: boolean;
  messageInput: string;
  onMessageChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onSendMessage: () => void;
  isSending: boolean;
  chatContainerRef: RefObject<HTMLDivElement | null>;
  chatEndRef: RefObject<HTMLDivElement | null>;
}

const SessionConversationPanel = ({
  visibleMessages,
  participants,
  syncAgo,
  hiddenCount,
  showSystemMessages,
  onToggleSystemMessages,
  isWaitingForReply,
  messageInput,
  onMessageChange,
  onKeyDown,
  onSendMessage,
  isSending,
  chatContainerRef,
  chatEndRef,
}: Props) => (
  <div className={styles.conversationPanel}>
    <div className={styles.conversationHeader}>
      <div className={styles.conversationHeaderTop}>
        <h3>Conversation</h3>
        <div className={styles.participantsList}>
          {participants.map(p => (
            <span
              key={p.name}
              className={`${styles.participantChip} ${p.role === "assistant" ? styles.participantBot : ""}`}
              title={p.isSlack ? `${p.name} (via Slack)` : p.name}
            >
              {p.role === "assistant" ? <span className={styles.botDot}>&#9679;</span> : null}
              {p.name}
              {p.isSlack ? <span className={styles.viaSlackTag}>(Slack)</span> : null}
            </span>
          ))}
        </div>
      </div>
      <div className={styles.conversationControls}>
        <div className={styles.messageCount}>
          {`${visibleMessages.length} message${visibleMessages.length !== 1 ? "s" : ""}`}
          {syncAgo && <span className={styles.syncIndicator}> · synced {syncAgo}</span>}
        </div>
        {(hiddenCount > 0 || showSystemMessages) && (
          <button
            type="button"
            className={styles.toggleSystemBtn}
            onClick={onToggleSystemMessages}
          >
            {showSystemMessages ? "Hide system messages" : `Show ${hiddenCount} hidden`}
          </button>
        )}
      </div>
    </div>

    <div className={styles.chatContainer} ref={chatContainerRef as React.RefObject<HTMLDivElement>}>
      {visibleMessages.map(message => {
        const isSystemOrTool =
          message.role === "system" || message.role === "tool" || message.role === "toolResult";

        if (isSystemOrTool) {
          return (
            <div key={message.id} className={`${styles.messageBubble} ${styles.systemMessage}`}>
              <div className={styles.systemDivider}>
                <span className={styles.systemLabel}>{getRoleLabel(message.role)}</span>
                {message.created_at && (
                  <span className={styles.messageTimestamp}>
                    {formatTime(message.created_at)}
                  </span>
                )}
              </div>
              {message.content && (
                <div className={styles.systemContent}>
                  {parseMessageContent(message.content).text.slice(0, 200)}
                </div>
              )}
            </div>
          );
        }

        return <MessageBubble key={message.id} message={message} />;
      })}

      {visibleMessages.length === 0 && (
        <div className={styles.emptyState}>
          <Typography variant="body">No messages in this session yet.</Typography>
        </div>
      )}

      {isWaitingForReply && (
        <div className={styles.typingIndicator}>
          <div className={styles.typingDots}>
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span className={styles.typingText}>Altimate AI is thinking...</span>
        </div>
      )}
      <div ref={chatEndRef as React.RefObject<HTMLDivElement>} />
    </div>

    <div className={styles.chatInputArea}>
      <textarea
        className={styles.chatInput}
        value={messageInput}
        onChange={e => onMessageChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
        rows={2}
        disabled={isSending}
      />
      <button
        type="button"
        className={styles.sendBtn}
        onClick={onSendMessage}
        disabled={!messageInput.trim() || isSending}
      >
        {isSending ? "Sending..." : "Send"}
      </button>
    </div>
  </div>
);

export default SessionConversationPanel;
